import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import cancelApproval from "@insite/client-framework/Store/Pages/Cart/Handlers/CancelApproval";
import discardEdit from "@insite/client-framework/Store/Pages/Cart/Handlers/DiscardEdit";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import saveOrder from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrder";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design.surya";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    fullWidthButton = "fullWidthButton",
    checkoutVariant = "checkoutVariant",
    requestForQuoteVariant = "requestForQuoteVariant",
    saveOrderVariant = "saveOrderVariant",
    removeAllVariant = "removeAllVariant",
}

const enum variants {
    primary = "primary",
    secondary = "secondary",
    overflow = "overflow",
    unenabled = "unenabled",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fullWidthButton]: boolean;
        [fields.checkoutVariant]: variants;
        [fields.requestForQuoteVariant]: variants;
        [fields.saveOrderVariant]: variants;
        [fields.removeAllVariant]: variants;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        cartSettings: { onePageCheckout },
    } = getSettingsCollection(state);
    const { isSavingOrder } = state.pages.cart;

    return {
        cart,
        isCartEmpty: isCartEmpty(cart),
        canSaveOrder: cartState.value?.canSaveOrder,
        isPunchOutOrder: isPunchOutOrder(cart),
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            isCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        isSignedIn: cartState.value?.isAuthenticated && !cartState.value?.isGuestOrder,
        savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        isSaveOrderDisabled:
            cartState.isLoading ||
            isSavingOrder ||
            isUpdatingCart ||
            cartState.value?.lineCount === 0 ||
            cartState.value?.cartLines?.length === 0,
        isClearingCart: state.pages.cart.isClearingCart,
        isApprovalEditDraft: cart?.status === "AwaitingApprovalEditDraft",
        isApprovalInProcess: cart?.orderApprovalStatus === "ApprovalInProcess",
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    saveOrder,
    signInAsGuest,
    clearCurrentCart,
    cancelApproval,
    discardEdit,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartActionsStyles {
    narrowHidden?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderClickable?: ClickablePresentationProps;
    requestAQuoteClickable?: ClickablePresentationProps;
    checkoutClickable?: ClickablePresentationProps;
    removeAllClickable?: ClickablePresentationProps;
    wideHidden?: HiddenProps;
    wideOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    saveOrderLink?: LinkPresentationProps;
    removeAllLink?: LinkPresentationProps;
    linkSeparator?: TypographyPresentationProps;
    checkoutButton?: ButtonPresentationProps;
    modalContainer?: ModalPresentationProps;
    modalBodyContainer?: GridContainerProps;
    modalContent?: TypographyPresentationProps;
    modalYesButton?: ButtonPresentationProps;
    closeButton?: ButtonPresentationProps;
    discardEditWrapper?: InjectableCss;
    discardEditButton?: ButtonPresentationProps;
    cancelApprovalWrapper?: InjectableCss;
    cancelApprovalButton?: ButtonPresentationProps;
}

export const cartActionsStyles: CartActionsStyles = {
    narrowHidden: {
        above: "sm",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    wideHidden: {
        below: "md",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    saveOrderLink: {
        css: css`
            text-decoration: none;
            color: ${COLORS.primary.base};
            font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            &:hover {
                text-decoration: underline;
            }

            span {
                color: ${COLORS.primary.base} !important;
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            }
        `,
    },
    removeAllLink: {
        css: css`
            text-decoration: none;
            color: ${COLORS.primary.base};
            font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            &:hover {
                text-decoration: underline;
            }

            span {
                color: ${COLORS.primary.base} !important;
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            }
        `,
    },
    linkSeparator: {
        css: css`
            margin: 0 10px;
            color: ${({ theme }) => theme.colors.text.disabled};
            user-select: none;
            font-family: ${TYPOGRAPHY.fontFamily.title} !important;
        `,
    },
    wideOverflowMenu: {
        cssOverrides: {
            wrapper: css`
                margin-left: 20px;
            `,
        },
    },
    discardEditWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    discardEditButton: {
        variant: "secondary",
    },
    cancelApprovalWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    cancelApprovalButton: {
        variant: "secondary",
    },
    modalContainer: {
        size: 450,
        cssOverrides: {
            modalTitle: css`
                border: 0px !important;
                padding: 10px 20px !important;
                background-color: #f8f8f8 !important;
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                font-weight: ${TYPOGRAPHY.weight.bold} !important;
                font-size: ${TYPOGRAPHY.size.larger} !important;
                line-height: ${TYPOGRAPHY.lineHeight.lmedium} !important;
                font-style: normal;

                h1,
                h2,
                h3,
                h4,
                h5,
                h6 {
                    font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                    font-weight: ${TYPOGRAPHY.weight.bold} !important;
                    font-size: ${TYPOGRAPHY.size.larger} !important;
                    line-height: ${TYPOGRAPHY.lineHeight.lmedium} !important;
                    font-style: normal;
                }
            `,
            modalContent: css`
                padding: 15px 20px;
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            `,
            modalBody: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 95%;
                            height: auto;
                        `,
                        css`
                            width: 75%;
                            height: auto;
                        `,
                        css`
                            width: 75%;
                            height: auto;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    modalBodyContainer: {
        css: css`
            display: flex;
            padding: 16px;
            margin-top: 15px;
        `,
        offsetCss: css`
            justify-content: right;
        `,
    },
    modalContent: {
        css: css`
            line-height: 24px;
            font-size: 15px;
            font-weight: 400;
            font-family: Mulish;
        `,
    },
    modalYesButton: {
        css: css`
            span[class^="TypographyStyle"] {
                color: white;
                font-size: 14px;
            }
        `,
    },
    closeButton: {
        buttonType: "outline",
        css: css`
            margin-left: 10px;
            span[class^="TypographyStyle"] {
                color: black;
                font-size: 14px;
            }
        `,
    },
};

const styles = cartActionsStyles;

const CartActions = ({
    cart,
    isCartEmpty,
    isPunchOutOrder,
    fields,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    hasOnlyQuoteRequiredProducts,
    canSubmitForQuote,
    isSubmitForQuoteButtonDisabled,
    history,
    rfqRequestQuotePageUrl,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    hasQuoteRequiredProducts,
    checkoutReviewAndSubmitPageUrl,
    checkoutShippingPageUrl,
    isSignedIn,
    saveOrder,
    savedOrdersPageUrl,
    cartPageUrl,
    signInPageUrl,
    canSaveOrder,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    isSaveOrderDisabled,
    isClearingCart,
    clearCurrentCart,
    isApprovalEditDraft,
    orderApprovalDetailsPageUrl,
    discardEdit,
    isApprovalInProcess,
    cancelApproval,
}: Props) => {
    if (!cart || isPunchOutOrder) {
        return null;
    }

    const toasterContext = useContext(ToasterContext);
    const [clearCartModalIsOpen, setClearCartModalIsOpen] = useState(false);

    const removeAllClickHandler = () => {
        setClearCartModalIsOpen(true);
    };

    const confirmClearCartHandler = () => {
        clearCurrentCart();
        setClearCartModalIsOpen(false);
    };

    const overflowItems = [];
    const wideHiddenOverflowMenu = [];
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [discardChangesModalIsOpen, setDiscardChangesModalIsOpen] = useState(false);

    const checkoutClickHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const requestForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    const saveOrderClickHandler = () => {
        if (!isSignedIn) {
            history.push(`${signInPageUrl}?returnUrl=${cartPageUrl}`);
            return;
        }

        saveOrder({
            onSuccess: (orderId: string) => {
                history.push(`${savedOrdersPageUrl}?cartId=${orderId}`);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
        });
    };

    if (isApprovalEditDraft) {
        const discardEditClickHandler = async () => {
            discardEdit({
                cart,
                onSuccess: () => {
                    if (orderApprovalDetailsPageUrl) {
                        history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.orderNumber.split("_")[1]}`);
                    }
                },
            });
        };

        return (
            <StyledWrapper {...styles.discardEditWrapper}>
                <Button
                    {...styles.discardEditButton}
                    onClick={() => setDiscardChangesModalIsOpen(true)}
                    data-test-selector="cartTotal_discardEdit"
                >
                    {translate("Discard edit")}
                </Button>
                <TwoButtonModal
                    headlineText={translate("Are you sure?")}
                    modalIsOpen={discardChangesModalIsOpen}
                    messageText={siteMessage("OrderApproval_DiscardEditConfirmationMessage")}
                    cancelButtonText={translate("Continue editing")}
                    submitButtonText={translate("Discard edit")}
                    onCancel={() => setDiscardChangesModalIsOpen(false)}
                    onSubmit={discardEditClickHandler}
                    dataTestSelector="discard-edit-modal"
                    cancelTestSelector="discard-edit-modal_cancel"
                    submitTestSelector="discard-edit-modal_discard"
                ></TwoButtonModal>
            </StyledWrapper>
        );
    }

    if (isApprovalInProcess) {
        const cancelApprovalClickHandler = async () => {
            cancelApproval({
                cart,
                onSuccess: () => {
                    if (orderApprovalDetailsPageUrl) {
                        history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.id}`);
                    }
                },
            });
        };

        return (
            <StyledWrapper {...styles.cancelApprovalWrapper}>
                <Button
                    {...styles.cancelApprovalButton}
                    onClick={cancelApprovalClickHandler}
                    data-test-selector="cartTotal_saveAndContinueLater"
                >
                    {translate("Save and continue later")}
                </Button>
            </StyledWrapper>
        );
    }

    const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Submit for Quote");

    if (canCheckoutWithCart && fields.checkoutVariant === variants.overflow) {
        overflowItems.push(
            <Clickable
                {...styles.checkoutClickable}
                key="checkout"
                onClick={checkoutClickHandler}
                disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
            >
                {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
            </Clickable>,
        );
    }

    if (canSubmitForQuote && fields.requestForQuoteVariant === variants.overflow) {
        overflowItems.push(
            <Clickable {...styles.requestAQuoteClickable} key="requestforquote" onClick={requestForQuoteClickHandler}>
                {submitForQuoteLabel}
            </Clickable>,
        );
    }

    if (canSaveOrder && fields.saveOrderVariant === variants.overflow) {
        overflowItems.push(
            <Clickable
                {...styles.saveOrderClickable}
                key="saveorder"
                onClick={saveOrderClickHandler}
                disabled={isSaveOrderDisabled}
            >
                {translate("Save Order")}
            </Clickable>,
        );
    }

    if (!isCartEmpty && fields.removeAllVariant === variants.overflow) {
        overflowItems.push(
            <Clickable
                {...styles.removeAllClickable}
                key="removeAll"
                disabled={isClearingCart}
                onClick={removeAllClickHandler}
                data-test-selector="cartActionsRemoveAll"
            >
                {translate("Clear Cart")}
            </Clickable>,
        );
    }

    if (overflowItems.length > 0) {
        wideHiddenOverflowMenu.push(
            <OverflowMenu key="overflow" {...styles.wideOverflowMenu} data-test-selector="cartActionsOverflowMenu">
                {overflowItems}
            </OverflowMenu>,
        );
    }

    const buttonCss = css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;
    return (
        <>
            <Hidden {...styles.narrowHidden} data-test-selector="cartActionsNarrow">
                <OverflowMenu
                    position="end"
                    {...styles.narrowOverflowMenu}
                    data-test-selector="cartActionsOverflowMenu"
                >
                    {canCheckoutWithCart &&
                        (fields.checkoutVariant === variants.primary ||
                            fields.checkoutVariant === variants.secondary) && (
                            <Button
                                {...styles.checkoutButton}
                                onClick={checkoutClickHandler}
                                disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                                variant={fields.checkoutVariant}
                                className="button"
                                css={buttonCss}
                            >
                                {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
                            </Button>
                        )}
                    {canSubmitForQuote &&
                        (fields.requestForQuoteVariant === variants.primary ||
                            fields.requestForQuoteVariant === variants.secondary) && (
                            <Button
                                {...styles.requestAQuoteClickable}
                                disabled={isSubmitForQuoteButtonDisabled}
                                onClick={requestForQuoteClickHandler}
                                variant={fields.requestForQuoteVariant}
                                className="button"
                                css={buttonCss}
                            >
                                {submitForQuoteLabel}
                            </Button>
                        )}
                    {canSaveOrder &&
                        fields.saveOrderVariant !== variants.unenabled &&
                        fields.saveOrderVariant !== variants.overflow && (
                            <Link
                                {...styles.saveOrderLink}
                                onClick={saveOrderClickHandler}
                                disabled={isSaveOrderDisabled}
                                data-test-selector="cart_saveOrder"
                            >
                                {translate("Save Order")}
                            </Link>
                        )}
                    {!isCartEmpty &&
                        fields.removeAllVariant !== variants.unenabled &&
                        fields.removeAllVariant !== variants.overflow && (
                            <Link
                                {...styles.removeAllLink}
                                onClick={removeAllClickHandler}
                                disabled={isClearingCart}
                                data-test-selector="cartActionsRemoveAll"
                            >
                                {translate("Clear Cart")}
                            </Link>
                        )}
                    {overflowItems}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.wideHidden} data-test-selector="cartActionsWide">
                {canCheckoutWithCart &&
                    (fields.checkoutVariant === variants.primary || fields.checkoutVariant === variants.secondary) && (
                        <Button
                            {...styles.checkoutButton}
                            onClick={checkoutClickHandler}
                            disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                            variant={fields.checkoutVariant}
                            className="button"
                            css={buttonCss}
                        >
                            {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
                        </Button>
                    )}
                {canSubmitForQuote &&
                    (fields.requestForQuoteVariant === variants.primary ||
                        fields.requestForQuoteVariant === variants.secondary) && (
                        <Button
                            {...styles.requestAQuoteClickable}
                            disabled={isSubmitForQuoteButtonDisabled}
                            onClick={requestForQuoteClickHandler}
                            variant={fields.requestForQuoteVariant}
                            className="button"
                            css={buttonCss}
                        >
                            {submitForQuoteLabel}
                        </Button>
                    )}
                {canSaveOrder &&
                    fields.saveOrderVariant !== variants.unenabled &&
                    fields.saveOrderVariant !== variants.overflow && (
                        <Link
                            {...styles.saveOrderLink}
                            onClick={saveOrderClickHandler}
                            disabled={isSaveOrderDisabled}
                            data-test-selector="cart_saveOrder"
                        >
                            {translate("Save Order")}
                        </Link>
                    )}
                {canSaveOrder &&
                    fields.saveOrderVariant !== variants.unenabled &&
                    fields.saveOrderVariant !== variants.overflow &&
                    !isCartEmpty &&
                    fields.removeAllVariant !== variants.unenabled &&
                    fields.removeAllVariant !== variants.overflow && (
                        <Hidden below="sm">
                            <Typography {...styles.linkSeparator}>|</Typography>
                        </Hidden>
                    )}
                {!isCartEmpty &&
                    fields.removeAllVariant !== variants.unenabled &&
                    fields.removeAllVariant !== variants.overflow && (
                        <Link
                            {...styles.removeAllLink}
                            onClick={removeAllClickHandler}
                            disabled={isClearingCart}
                            data-test-selector="cartActionsRemoveAll"
                        >
                            {translate("Clear Cart")}
                        </Link>
                    )}
                {wideHiddenOverflowMenu}
            </Hidden>
            <TwoButtonModal
                headlineText=""
                modalIsOpen={quoteRequiredModalIsOpen}
                messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={() => setQuoteRequiredModalIsOpen(false)}
                onSubmit={checkoutClickHandler}
                submitTestSelector="QuoteInCartMessageSubmit"
                dataTestSelector="twoButtonModal_RequiresQuote"
            ></TwoButtonModal>
            <Modal
                {...styles.modalContainer}
                isOpen={clearCartModalIsOpen}
                isCloseable={false}
                sizeVariant="small"
                headline={translate("Confirm")}
            >
                <Typography {...styles.modalContent}>
                    {translate("Are you sure you want to remove all items from your cart?")}
                </Typography>
                <GridContainer {...styles.modalBodyContainer}>
                    <Button {...styles.modalYesButton} onClick={confirmClearCartHandler}>
                        {translate("Yes")}
                    </Button>
                    <Button {...styles.closeButton} onClick={() => setClearCartModalIsOpen(false)}>
                        {translate("Cancel")}
                    </Button>
                </GridContainer>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartActions)),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Buttons",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            },
            {
                name: fields.checkoutVariant,
                displayName: "Checkout",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary Action", value: variants.primary },
                    { displayName: "Secondary Action", value: variants.secondary },
                    { displayName: "Overflow Action", value: variants.overflow },
                    { displayName: "Not Enabled", value: variants.unenabled },
                ],
                defaultValue: variants.unenabled,
                fieldType: "General",
            },
            {
                name: fields.requestForQuoteVariant,
                displayName: "Submit for Quote",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary Action", value: variants.primary },
                    { displayName: "Secondary Action", value: variants.secondary },
                    { displayName: "Overflow Action", value: variants.overflow },
                    { displayName: "Not Enabled", value: variants.unenabled },
                ],
                defaultValue: variants.unenabled,
                fieldType: "General",
            },
            {
                name: fields.saveOrderVariant,
                displayName: "Save Order",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary Action", value: variants.primary },
                    { displayName: "Secondary Action", value: variants.secondary },
                    { displayName: "Overflow Action", value: variants.overflow },
                    { displayName: "Not Enabled", value: variants.unenabled },
                ],
                defaultValue: variants.secondary,
                fieldType: "General",
            },
            {
                name: fields.removeAllVariant,
                displayName: "Clear Cart",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary Action", value: variants.primary },
                    { displayName: "Secondary Action", value: variants.secondary },
                    { displayName: "Overflow Action", value: variants.overflow },
                    { displayName: "Not Enabled", value: variants.unenabled },
                ],
                defaultValue: variants.overflow,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
